/**
 * <Modal> — the maison form-modal shell (molecule, L0.1).
 *
 * A generic dialog/bottom-sheet that owns everything a form modal needs so consumers can't forget it:
 * a `<Header>` (title + `headerLeft` / `headerRight` slots; left defaults to close), a scrollable body,
 * a pinned `footer` slot, keyboard avoidance, safe-area insets, and responsive layout. The portal/scrim/backdrop/back+Escape plumbing lives in the shared
 * `<Overlay>` atom (a JS-layer paper `<Portal>` — no native iOS `UIViewController`, which is what lets an
 * in-modal image picker present normally). Modal just styles + positions its own panel inside it.
 *
 * Layout: full-height bottom sheet on small screens (`fullScreenOnSmall`), a centered card otherwise.
 * `fitContent` forces a centered, content-sized dialog (capped with max-height) — used by short surveys.
 * `sizeToContent` garde la disposition (feuille en bas / carte centrée) mais fait la panneau de la
 * HAUTEUR DE SON CONTENU, plafonnée. Une étape qui montre trois lignes n'a pas à occuper 90 % de
 * l'écran : la feuille pleine hauteur ne se justifie que quand le contenu la remplit.
 * Header pinned · body scrolls · footer pinned. Dismisses on backdrop tap, Android hardware back, and web
 * Escape (all gated by `dismissable`, handled by `<Overlay>`).
 *
 * `steps` en fait l'étape d'un parcours, sous UN seul en-tête : la question de l'étape en titre
 * (`title`), le retour à gauche, la croix à droite, et une barre de progression fine à la place du
 * filet. Le nom du geste, lui, ne s'affiche plus : il passe par `accessibilityLabel`, qui nomme la
 * feuille pour un lecteur d'écran.
 */
import React, { useContext } from "react"
import { Dimensions, KeyboardAvoidingView, Platform, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { useFocusedFieldInView } from "../../hooks/useFocusedFieldInView"
import { Overlay, SHEET_EDGE_WIDTH } from "../../atoms/Overlay"
import { Header, type HeaderSteps } from "../Header"
import { IconButton } from "../../atoms/Buttons/IconButton"

/** L'étape courante, le nombre d'étapes et le retour ; la croix est celle de la feuille (`onClose`). */
export type ModalSteps = Omit<HeaderSteps, "onClose">

export type ModalProps = {
    visible: boolean
    onClose: () => void
    /** Le titre affiché dans l'en-tête — sous `steps`, la question de l'étape. */
    title: string
    /**
     * Le nom de la feuille pour un lecteur d'écran, quand l'en-tête affiche autre chose que ce
     * nom — le nom du geste au-dessus de la question d'une étape. Défaut : `title`.
     */
    accessibilityLabel?: string
    /**
     * Tête d'un parcours en étapes : la croix passe à droite, la flèche de retour prend la gauche
     * quand `onBack` est donné, le titre passe à la ligne au lieu d'être coupé, et une barre fine
     * remplace le filet dès qu'il y a plus d'une étape (« Étape 2 sur 3 » est dit au lecteur
     * d'écran, pas écrit). `headerLeft` / `headerRight` ne s'y appliquent pas. Absent → la tête
     * d'avant.
     */
    steps?: ModalSteps
    /** Full-height bottom sheet on small screens (< 800px); centered card otherwise. Defaults to false. */
    fullScreenOnSmall?: boolean
    /**
     * Centered dialog sized to its content (capped with max-height). Use for short surveys (NPS).
     * Overrides bottom-sheet / tall-panel sizing. Defaults to false.
     */
    fitContent?: boolean
    /**
     * Panneau à la hauteur de son contenu (plafonné à 85 %), sans changer la disposition : la
     * feuille reste en bas sur un téléphone, la carte reste centrée en grand écran. Défaut `false`.
     */
    sizeToContent?: boolean
    /**
     * Grand écran : une carte large et haute (1 200 px, bornée à 94 % de la fenêtre dans les deux
     * sens) au lieu des 500 px, pour poser deux colonnes côte à côte — l'énoncé et la copie
     * corrigée d'une activité, qui ont besoin de toute la place. Sans effet sur un téléphone.
     * Défaut `false`.
     */
    wide?: boolean
    /** Pinned footer below the scroll area (e.g. a submit `<Button>`). */
    footer?: React.ReactNode
    /**
     * Leading header action. Defaults to a times close button that calls `onClose`.
     * Pass a back `<IconButton>` (etc.) for nested modal steps.
     */
    headerLeft?: React.ReactNode
    /** Extra header action on the right (e.g. a refresh `<IconButton>`). Optional — the title stays centered without it. */
    headerRight?: React.ReactNode
    /** Scroll the body. Defaults to true. */
    scrollable?: boolean
    /** The body's `<ScrollView>`, for a form that brings a field into view. Ignored when not `scrollable`. */
    scrollRef?: React.Ref<ScrollView>
    /** Backdrop tap + Android back + web Escape close the modal. Defaults to false. */
    dismissable?: boolean
    /**
     * Hide without unmounting (camera / Genius Scan trip). Forwarded to `<Overlay hidden>`.
     * Keeps form state alive while the portal stops covering the camera UI.
     */
    hidden?: boolean
    children: React.ReactNode
    testID?: string
}

export function Modal(props: ModalProps) {
    const {
        visible,
        onClose,
        title,
        accessibilityLabel,
        steps,
        fullScreenOnSmall = false,
        fitContent = false,
        sizeToContent = false,
        wide = false,
        footer,
        headerLeft,
        headerRight,
        scrollable = true,
        scrollRef: scrollRefProp,
        dismissable = false,
        hidden = false,
        children,
        testID,
    } = props

    // Read the context here (outside the Portal, where the app's provider is in scope); fall back to the
    // raw window dims for Storybook / no-provider. We re-provide it inside the Portal below.
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const insets = useSafeAreaInsets()
    const isLarge = dimensions.width > 800
    const scrollRef = useFocusedFieldInView(visible, scrollRefProp)
    const bottomSheet = !isLarge && fullScreenOnSmall && !fitContent
    const centered = fitContent || isLarge

    // Le panneau se mesure sur son contenu dans les deux dispositions.
    const hugged = fitContent || sizeToContent

    const left = headerLeft ?? (
        <IconButton
            iconName="times"
            appearance="transparent"
            iconColor="primary"
            onPress={onClose}
            accessibilityLabel="Fermer"
        />
    )
    // No spacer needed for a missing trailing action — `<Header>` centers its title on its own.

    const header =
        steps != null ? (
            <Header fullWidth title={title} steps={{ ...steps, onClose }} />
        ) : (
            <Header fullWidth title={title} left={left} right={headerRight} />
        )

    const body = scrollable ? (
        <ScrollView
            ref={scrollRef}
            style={hugged ? styles.bodyScrollFit : styles.flex}
            contentContainerStyle={styles.body(steps != null)}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            // Only on the content-sized panel: rubber-banding an auto-height dialog reads as broken.
            // Tall/sheet modals keep the platform-default bounce.
            bounces={!hugged}
        >
            {children}
        </ScrollView>
    ) : (
        <View style={[hugged ? undefined : styles.flex, styles.body(steps != null)]}>{children}</View>
    )

    return (
        <Overlay
            visible={visible}
            onRequestClose={onClose}
            dismissable={dismissable}
            hidden={hidden}
            placement={centered ? "center" : "bottom"}
            testID={testID}
        >
            <KeyboardAvoidingView
                // Android: SystemBarsFrame already ends the app at the keyboard.
                behavior={Platform.OS === "ios" ? "padding" : undefined}
                style={[styles.panel(isLarge, bottomSheet, hugged, insets.top, wide), !centered && styles.risingEdge]}
                role="dialog"
                aria-label={accessibilityLabel ?? title}
            >
                {header}
                {body}
                {footer != null && <View style={styles.footer(insets.bottom)}>{footer}</View>}
            </KeyboardAvoidingView>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    panel: (isLarge: boolean, bottomSheet: boolean, hugged: boolean, topInset: number, wide: boolean) => ({
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        borderTopLeftRadius: theme.radius.lg,
        borderTopRightRadius: theme.radius.lg,
        borderBottomLeftRadius: bottomSheet ? 0 : theme.radius.lg,
        borderBottomRightRadius: bottomSheet ? 0 : theme.radius.lg,
        width: isLarge ? (wide ? 1200 : 500) : bottomSheet ? "100%" : "90%",
        // Contenu → hauteur auto plafonnée ; feuille → remplit ; carte par défaut → % fixe.
        height: hugged ? undefined : bottomSheet ? undefined : isLarge ? (wide ? "94%" : "90%") : "85%",
        maxHeight: hugged ? "85%" : undefined,
        flex: bottomSheet && !hugged ? 1 : undefined,
        // Clear the notch on the full-height sheet (the centered card floats away from the edges).
        marginTop: bottomSheet ? topInset + theme.spacing["2"] : 0,
        maxWidth: isLarge && wide ? ("94%" as const) : ("100%" as const),
    }),
    /**
     * Une feuille qui monte du bas porte un liseré sur son bord haut (`sheet-edge`) : sur la nuit
     * (élève et parent), il la détache du voile que sa surface ne peut plus distancer ; en clair il
     * vaut la surface de la feuille et ne se voit pas.
     */
    risingEdge: {
        borderTopWidth: SHEET_EDGE_WIDTH,
        borderColor: theme.colors["sheet-edge"],
    },
    flex: {
        flex: 1,
    },
    /** Grow with content; shrink if the panel hits maxHeight so ScrollView can scroll. */
    bodyScrollFit: {
        flexGrow: 0,
        flexShrink: 1,
    },
    /** Sous une tête d'étape, un espacement de section entre la barre et la première ligne. */
    body: (stepMode: boolean) => ({
        padding: theme.spacing["5"],
        paddingTop: theme.spacing[stepMode ? "4" : "5"],
    }),
    footer: (bottomInset: number) => ({
        padding: theme.spacing["5"],
        // Clear the home indicator — the legacy modal never padded the bottom inset.
        paddingBottom: theme.spacing["5"] + bottomInset,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    }),
}))
